import { Delete } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  styled,
} from "@mui/material";
import { FormikErrors, FormikTouched, useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, useMemo, useState } from "react";
import * as Yup from "yup";

import { parseTextAndUrlSegments, replaceURLs } from "@stw/extract-urls";
import LiquidTemplate from "@stw/liquid-template";
import {
  hasOptOutLanguage,
  messagingStrategyRequiresOptOutLanguage,
} from "@stw/message-script-validator";
import { EncodingStats, getEncodingStats } from "@stw/sms-text-encoding";

import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { submitErrorField } from "~frontend/components/forms/FormikErrorBanner";
import { mediaValidationSchema } from "~frontend/components/forms/FormikMediaField";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";

import { SendPreviewFormDialog } from "../../campaignView/initialSend/SendPreviewFormDialog";
import { CampaignEditorValues } from "../InitialMessage";
import { CampaignForEditor } from "../types";
import ScriptAlert from "./ScriptAlert";
import {
  PersonalizedGifFieldValue,
  ScriptEditor,
  generateSampleContact,
} from "./ScriptEditor";
import ShaftWarnings from "./ShaftWarnings";
import { ScriptVariantInput } from "./types";
import useOptOutPhraseList from "./useOptOutPhraseList";

export const ScriptVariantWrapper = ({
  label,
  children,
  showDelete,
  onDelete,
}: {
  label: string;
  children: React.ReactElement;
  showDelete: boolean;
  onDelete: () => void;
}): ReactElement => {
  return (
    <Card>
      <CardHeader
        title={label}
        action={
          showDelete && (
            <IconButton aria-label="remove script variant" onClick={onDelete}>
              <Delete />
            </IconButton>
          )
        }
      />
      <CardContent
        sx={{
          p: 0,
          pb: "0 !important",
          backgroundColor: palette.lightestGray,
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

export function addClientSideMediaUploadValidation(
  yup: Yup.AnyObjectSchema
): Yup.AnyObjectSchema {
  return yup.concat(
    Yup.object({
      media: mediaValidationSchema(),
    })
  );
}

export const ScriptFormEditorWithPreview = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const ScriptVariant = ({
  variant,
  hasMultiple,
  index,
  campaign,
  error,
  underGifLimit,
  onDelete,
  revealSendStatus,
}: {
  variant: ScriptVariantInput;
  hasMultiple: boolean;
  index: number;
  campaign: CampaignForEditor;
  underGifLimit: boolean;
  error?: string;
  onDelete: () => void;
  revealSendStatus: () => void;
}): ReactElement => {
  const tenant = useTenant()!;
  const subaccount = useSubaccountAdminConfig()!;

  const formik = useFormikContext();

  const [previewFormOpen, setPreviewFormOpen] = useState<null | {
    contactData: Record<string, string>;
  }>(null);

  const personalizedGifFieldValue: PersonalizedGifFieldValue =
    formik.getFieldProps(`variants.${index}.personalizedGif`).value;

  const linkShortenerDomainName = variant.overrideLinkShortenerSettings
    ? variant.linkShortenerDomainName
    : subaccount.defaultLinkShortenerDomainName || undefined;

  const hasAttachments = !!(variant.media?.file || variant.media?.upload);

  const encodingStats = useMemo<EncodingStats | null>(() => {
    const parsedTemplate = LiquidTemplate.fromString(variant.script);

    if (parsedTemplate.ok) {
      let personalizedGifContactFields: string[] = [];
      if (personalizedGifFieldValue?.campaignPersonalizedGif) {
        personalizedGifContactFields =
          personalizedGifFieldValue.campaignPersonalizedGif.creatomateTemplate.requiredContactFields.map(
            field => field.name
          );
      } else if (personalizedGifFieldValue?.personalizedGif) {
        personalizedGifContactFields =
          personalizedGifFieldValue.personalizedGif.creatomateTemplate.requiredContactFields.map(
            field => field.name
          );
      }

      const sampleContact = generateSampleContact({
        parsedTemplate: parsedTemplate.template,
        sampleContactData: campaign.sampleContact?.data
          ? formatContactData({
              contactData: JSON.parse(campaign.sampleContact.data),
              campaignContactFields: campaign.contactFields,
            }).formattedData
          : {},
        personalizedGifContactFields,
      });

      const renderResult = parsedTemplate.template.render(sampleContact, {
        refcode2w: "XXXXXX",
        autoAddRefcodesToABLinks: subaccount.autoAddRefcodesToABLinks,
      });

      if (renderResult.ok) {
        const stats = getEncodingStats(
          {
            text: renderResult.result,
            hasAttachments,
          },
          tenant
        );

        if (linkShortenerDomainName) {
          // Extract URLs
          const textAndUrlSegments = parseTextAndUrlSegments(
            stats.smartEncoded
          );

          // Recompute encoding stats
          const placeholderShortenedLink = `https://${linkShortenerDomainName}/${_.repeat(
            "x",
            6
          )}`;
          return getEncodingStats(
            {
              text: replaceURLs(
                textAndUrlSegments,
                () => placeholderShortenedLink
              ).join(""),
              hasAttachments,
            },
            tenant
          );
        } else {
          return stats;
        }
      }
    }
    return null;
  }, [
    variant,
    personalizedGifFieldValue,
    campaign,
    subaccount,
    tenant,
    hasAttachments,
    linkShortenerDomainName,
  ]);

  const optOutPhraseList = useOptOutPhraseList();

  const label =
    variant.displayOrder != null
      ? `Variant ${variant.displayOrder}`
      : "Unsaved Variant";

  return (
    <ScriptVariantWrapper
      label={label}
      showDelete={hasMultiple}
      onDelete={onDelete}
    >
      <Box>
        <FlexBox direction="column" gap={2} sx={{ py: 1.5, px: 2 }}>
          <ScriptEditor
            type="campaignInitial"
            script={variant.script}
            media={variant.media}
            campaign={campaign}
            fieldPrefix={`variants.${index}`}
            underGifLimit={underGifLimit}
            linkShortenerDomainName={linkShortenerDomainName}
            onSendPreviewMessage={({ contactData }) => {
              setPreviewFormOpen({ contactData });
            }}
            sendPreviewDisabled={
              formik.dirty || !variant.id
                ? "You must save your initial message before sending a preview text"
                : null
            }
          />
        </FlexBox>
        {!error &&
          encodingStats &&
          encodingStats.charCount > encodingStats.maxCharsForWarning &&
          encodingStats.charCount <= encodingStats.maxChars && (
            <ScriptAlert>
              <strong>Warning:</strong> messages with over{" "}
              {encodingStats.maxCharsForWarning.toLocaleString()} characters
              risk lower deliverability rates
            </ScriptAlert>
          )}
        {!error && encodingStats && (
          <ShaftWarnings renderedMessage={encodingStats.smartEncoded} />
        )}
        {!error &&
          encodingStats &&
          encodingStats.charCount > encodingStats.maxChars && (
            <ScriptAlert>
              Message exceeds maximum of{" "}
              {encodingStats.maxChars.toLocaleString()} characters
            </ScriptAlert>
          )}
        {error && <ScriptAlert>{error}</ScriptAlert>}
        <AsyncWrapper
          loading={optOutPhraseList.loading}
          data={optOutPhraseList.phraseList}
          errors={null}
        >
          {optOutPhrases =>
            variant.script.length > 0 &&
            !messagingStrategyRequiresOptOutLanguage(
              campaign.messagingStrategy.strategy
            ) &&
            !hasOptOutLanguage(variant.script, {
              optOutPhrases: optOutPhrases!,
            }) && (
              <ScriptAlert severity="warning">
                <strong>Warning:</strong> Your script does not contain opt-out
                language. To comply with best practices and carrier
                requirements, consider including opt-out language in your
                campaign.
              </ScriptAlert>
            )
          }
        </AsyncWrapper>
        {previewFormOpen && (
          <SendPreviewFormDialog
            campaign={campaign}
            handleClose={() => setPreviewFormOpen(null)}
            revealSendStatus={revealSendStatus}
            sampleContactData={previewFormOpen.contactData}
            singleVariant={variant.id!}
            singleVariantIndex={variant.displayOrder ?? undefined}
          />
        )}
      </Box>
    </ScriptVariantWrapper>
  );
};

export const ScriptVariants = ({
  campaign,
  values,
  errors,
  touched,
  onDelete,
  revealSendStatus,
}: {
  campaign: CampaignForEditor;
  values: ScriptVariantInput[];
  errors?: FormikErrors<CampaignEditorValues>;
  touched?: FormikTouched<CampaignEditorValues>;
  onDelete: (index: number) => void;
  revealSendStatus: () => void;
}): ReactElement => {
  const hasMoreThanOneVariant = values.length > 1;

  const underGifLimit = values.filter(v => v.personalizedGif).length < 2;

  return (
    <FlexBox direction="column" gap={3}>
      {values.map((variant, index) => {
        let variantErrorMessage;
        if (touched?.variants?.[index]) {
          const variantErrors = errors?.variants?.[index];
          if (typeof variantErrors === "string") {
            variantErrorMessage = variantErrors;
          } else if (variantErrors) {
            variantErrorMessage = variantErrors[submitErrorField];
          }
        }

        return (
          <ScriptVariant
            key={`script-variant-${index}`}
            variant={variant}
            hasMultiple={hasMoreThanOneVariant}
            index={index}
            campaign={campaign}
            error={variantErrorMessage}
            underGifLimit={underGifLimit}
            onDelete={() => onDelete(index)}
            revealSendStatus={revealSendStatus}
          />
        );
      })}
    </FlexBox>
  );
};
